import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  Stack,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ReportCreateModal from '../components/ReportCreateModal';
import ReportTypeSelectionModal from '../components/ReportTypeSelectionModal';
import { ReportFormData, ReportType } from '../types';

const ReportCreate: React.FC = () => {
  const { user, hasRole } = useAuth();
  const [isTypeSelectionOpen, setIsTypeSelectionOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<'hourly' | 'work' | 'weekly' | 'user' | 'external' | 'summary' | 'call' | 'risk' | 'livechat' | null>(null);
  const [reports, setReports] = useState<ReportFormData[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Rol kontrolü - tüm kullanıcılar erişebilir
  if (!hasRole(['admin', 'developer', 'mod', 'call_admin', 'call', 'livechat_admin', 'livechat', 'risk_admin', 'risk'])) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          Bu sayfaya erişim yetkiniz bulunmamaktadır.
        </Alert>
      </Container>
    );
  }

  const handleCreateReport = (data: ReportFormData) => {
    const newReport = {
      ...data,
      id: Date.now().toString()
    };
    
    setReports(prev => [newReport, ...prev]);
    setSuccessMessage('Rapor başarıyla oluşturuldu!');
    
    // Success mesajını 3 saniye sonra gizle
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleOpenTypeSelection = () => {
    setIsTypeSelectionOpen(true);
  };

  const handleTypeSelect = (type: 'hourly' | 'work' | 'weekly' | 'user' | 'external' | 'summary' | 'call' | 'risk' | 'livechat') => {
    setSelectedType(type);
    setIsTypeSelectionOpen(false);
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setSelectedType(null);
  };

  const getReportTypeLabel = (type: ReportType): string => {
    const labels: Record<ReportType, string> = {
      [ReportType.DAILY]: 'Günlük Rapor',
      [ReportType.MONTHLY]: 'Aylık Rapor',
      [ReportType.SALES]: 'Satış Raporu',
      [ReportType.CALL]: 'Arama Raporu',
      [ReportType.LIVECHAT]: 'LiveChat Raporu',
      [ReportType.RISK]: 'Risk Raporu',
      [ReportType.HOURLY]: 'Saatlik Rapor',
      [ReportType.WORK_SUMMARY]: 'Mesai Özeti',
      [ReportType.WEEKLY]: 'Haftalık Rapor',
      [ReportType.USER_REPORT]: 'Kullanıcı Raporu',
      [ReportType.EXTERNAL_REPORT]: 'Dış Rapor',
      [ReportType.WEEKLY_SUMMARY]: 'Haftalık Özet Raporu'
    };
    return labels[type] || type;
  };

  const getReportTypeColor = (type: ReportType): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const colors: Record<ReportType, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      [ReportType.DAILY]: 'primary',
      [ReportType.MONTHLY]: 'secondary',
      [ReportType.SALES]: 'success',
      [ReportType.CALL]: 'info',
      [ReportType.LIVECHAT]: 'warning',
      [ReportType.RISK]: 'error',
      [ReportType.HOURLY]: 'info',
      [ReportType.WORK_SUMMARY]: 'warning',
      [ReportType.WEEKLY]: 'success',
      [ReportType.USER_REPORT]: 'primary',
      [ReportType.EXTERNAL_REPORT]: 'secondary',
      [ReportType.WEEKLY_SUMMARY]: 'success'
    };
    return colors[type] || 'default';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3,
        p: 4,
        mb: 4,
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              📊 Rapor Yönetimi
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Yeni rapor oluşturun ve mevcut raporlarınızı görüntüleyin.
            </Typography>
          </Box>
          <Box sx={{ 
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 2,
            p: 2,
            textAlign: 'center'
          }}>
            <Typography variant="h6" fontWeight="bold">
              {reports.length}
            </Typography>
            <Typography variant="caption">
              Toplam Rapor
            </Typography>
          </Box>
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Rapor Oluştur Butonu */}
        <Card sx={{ 
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          borderRadius: 3
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  🚀 Yeni Rapor Oluştur
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, color: 'rgba(255,255,255,0.9)' }}>
                  Detaylı rapor oluşturmak için aşağıdaki butona tıklayın.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleOpenTypeSelection}
                size="large"
                sx={{
                  background: 'rgba(255,255,255,0.15)',
                  color: '#333',
                  fontWeight: 'bold',
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    background: 'rgba(255,255,255,0.25)',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease'
                  }
                }}
              >
                Rapor Oluştur
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Oluşturulan Raporlar */}
        {reports.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Oluşturulan Raporlar ({reports.length})
            </Typography>
            <Stack spacing={2}>
              {reports.map((report, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" component="h3">
                      {report.title}
                    </Typography>
                    <Chip
                      label={getReportTypeLabel(report.type)}
                      color={getReportTypeColor(report.type)}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {report.type === ReportType.HOURLY 
                      ? `Tarih: ${report.date?.format('DD/MM/YYYY HH:mm')}`
                      : `Tarih: ${report.date?.format('DD/MM/YYYY')}`
                    }
                  </Typography>
                  
                  {/* Risk kullanıcıları için özel bilgiler */}
                  {(report.type === ReportType.HOURLY || report.type === ReportType.WORK_SUMMARY) && (
                    <Box sx={{ mb: 1 }}>
                      {report.totalHours && (
                        <Typography variant="body2" color="text.secondary">
                          Çalışma Saati: {report.totalHours} saat
                        </Typography>
                      )}
                      {report.hourlyRate && (
                        <Typography variant="body2" color="text.secondary">
                          Saatlik Ücret: {report.hourlyRate} TL
                        </Typography>
                      )}
                      {report.totalHours && report.hourlyRate && (
                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                          Toplam: {(report.totalHours * report.hourlyRate).toFixed(2)} TL
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  <Typography variant="body2" sx={{ 
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {report.content}
                  </Typography>
                  
                  {/* Saatlik ve mesai özeti */}
                  {report.hourlySummary && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Saatlik Özet:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: '0.875rem'
                      }}>
                        {report.hourlySummary}
                      </Typography>
                    </Box>
                  )}
                  
                  {report.workSummary && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Mesai Özeti:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: '0.875rem'
                      }}>
                        {report.workSummary}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Boş durum */}
        {reports.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Henüz rapor oluşturulmamış
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              İlk raporunuzu oluşturmak için yukarıdaki butona tıklayın.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleOpenTypeSelection}
              sx={{
                fontWeight: 'bold'
              }}
            >
              İlk Raporu Oluştur
            </Button>
          </Paper>
        )}
      </Stack>

      {/* Rapor Türü Seçim Modal */}
        <ReportTypeSelectionModal
          open={isTypeSelectionOpen}
          onClose={() => setIsTypeSelectionOpen(false)}
          onSelectType={handleTypeSelect}
          userRole={user?.role}
        />

      {/* Rapor Oluşturma Modal */}
      <ReportCreateModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateReport}
        selectedType={selectedType || undefined}
      />
    </Container>
  );
};

export default ReportCreate;
